import React, { useCallback, useEffect, useState } from "react";
import { FaChevronDown, FaChevronRight, FaPlus, FaTrash, FaPen, FaArrowUp, FaArrowDown, FaTimes, FaLock } from "react-icons/fa";
import { api } from "./schreibenUtils";
import { API_BASE } from "../../../api/config";

const KUENDIGUNG_API = `${API_BASE}/mietverwaltung/api/kuendigung`;

/* Themen-Akkordeon + Baustein-Verwaltung (Welle 90).
   Kündigungs-Bausteine (geschuetzt) sind editierbar, aber nicht lösch-/verschiebbar. */
export default function ThemenVerwaltung({ onClose }) {
  const [themen, setThemen] = useState([]);
  const [offen, setOffen] = useState(() => new Set());
  const [fehler, setFehler] = useState("");
  const [neuName, setNeuName] = useState("");
  const [editor, setEditor] = useState(null);   // {id?, thema_id, titel, betreff, text}
  const [config, setConfig] = useState(null);   // Ablage-Regeln (aus dem alten Modal übernommen)

  const laden = useCallback(async () => {
    try {
      const d = await api("/themen");
      setThemen(d.themen || []);
      const rc = await fetch(`${KUENDIGUNG_API}/config`);
      if (rc.ok) setConfig(await rc.json());
    } catch (e) { setFehler(String(e.message || e)); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const klappen = (id) => setOffen((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const aktion = async (fn) => {
    setFehler("");
    try { await fn(); await laden(); }
    catch (e) { setFehler(String(e.message || e)); }
  };

  const themaNeu = () => {
    const name = neuName.trim();
    if (!name) return;
    aktion(async () => { await api("/themen", "POST", { name }); setNeuName(""); });
  };

  const themaUmbenennen = (t) => {
    const name = window.prompt("Neuer Name für das Thema:", t.name);
    if (name && name.trim() && name.trim() !== t.name) {
      aktion(() => api(`/themen/${t.id}`, "PUT", { name: name.trim() }));
    }
  };

  const themaLoeschen = (t) => {
    if (!window.confirm(`Thema „${t.name}" löschen?`)) return;
    aktion(() => api(`/themen/${t.id}`, "DELETE"));
  };

  const verschieben = (t, idx, richtung) => {
    const ids = t.bausteine.map((b) => b.id);
    const ziel = idx + richtung;
    if (ziel < 0 || ziel >= ids.length) return;
    [ids[idx], ids[ziel]] = [ids[ziel], ids[idx]];
    aktion(() => api(`/themen/${t.id}/bausteine/sortierung`, "PUT", { ids }));
  };

  const bausteinLoeschen = (b) => {
    if (!window.confirm(`Baustein „${b.titel || b.key}" löschen?`)) return;
    aktion(() => api(`/bausteine/${b.id}`, "DELETE"));
  };

  const editorSpeichern = () => aktion(async () => {
    const { id, thema_id, titel, betreff, text } = editor;
    if (id) await api(`/bausteine/${id}`, "PUT", { titel, betreff, text });
    else await api("/bausteine", "POST", { thema_id, titel, betreff, text });
    setEditor(null);
  });

  return (
    <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div className="me-drawer" style={{ maxWidth: 760, width: "94vw", height: "auto", maxHeight: "88vh", overflow: "auto", borderRadius: 12, padding: 18 }}
           onClick={(e) => e.stopPropagation()}>
        <div className="me-drawer-header">
          <h3>🧩 Textbausteine & Themen</h3>
          <button className="me-btn me-btn-icon" onClick={onClose}><FaTimes /></button>
        </div>
        {fehler && <div className="me-status error">❌ {fehler}</div>}

        <div style={{ display: "flex", gap: 8, margin: "10px 0" }}>
          <input className="me-filter-input" style={{ flex: 1 }} placeholder="Neues Thema, z.B. Umzugsmitteilung…"
                 value={neuName} onChange={(e) => setNeuName(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && themaNeu()} />
          <button className="me-btn me-btn-primary me-btn-sm" disabled={!neuName.trim()} onClick={themaNeu}>
            <FaPlus /> Thema anlegen
          </button>
        </div>

        {themen.map((t) => (
          <div key={t.id ?? "ohne"} style={{ border: "1px solid #334155", borderRadius: 8, marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", cursor: "pointer" }}
                 onClick={() => klappen(t.id)}>
              {offen.has(t.id) ? <FaChevronDown /> : <FaChevronRight />}
              <b style={{ flex: 1 }}>{t.name}</b>
              <span style={{ color: "#94a3b8", fontSize: 12 }}>{t.bausteine.length} Baustein{t.bausteine.length === 1 ? "" : "e"}</span>
              {t.system ? <FaLock title="System-Thema" style={{ color: "#94a3b8" }} /> : (
                <>
                  {/* Welle 109: ein Thema kann statt eines Briefs einen Ablauf starten */}
                  <select className="vg-in" style={{ height: 26, fontSize: 12 }} value={t.prozess || ""}
                          title="Was „Neuer Vorgang“ mit diesem Thema startet"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => aktion(() => api(`/themen/${t.id}`, "PUT", { prozess: e.target.value }))}>
                    <option value="">startet: Brief</option>
                    <option value="neuer_mieter">startet: Ablauf „Neuer Mieter“</option>
                  </select>
                  <button className="me-btn me-btn-icon" title="Umbenennen"
                          onClick={(e) => { e.stopPropagation(); themaUmbenennen(t); }}><FaPen /></button>
                  <button className="me-btn me-btn-icon me-btn-danger" title="Löschen"
                          onClick={(e) => { e.stopPropagation(); themaLoeschen(t); }}><FaTrash /></button>
                </>
              )}
            </div>
            {offen.has(t.id) && (
              <div style={{ padding: "0 10px 10px 30px" }}>
                {t.bausteine.map((b, idx) => (
                  <div key={b.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", borderTop: "1px solid #1e293b" }}>
                    <span style={{ flex: 1 }}>
                      {b.titel || b.key}
                      {b.geschuetzt && <FaLock title="Kündigungs-Baustein — nicht löschbar" style={{ color: "#94a3b8", marginLeft: 6, fontSize: 11 }} />}
                      {b.betreff && <span style={{ color: "#94a3b8", marginLeft: 8, fontSize: 12 }}>· {b.betreff}</span>}
                    </span>
                    <button className="me-btn me-btn-icon" title="Nach oben" disabled={idx === 0}
                            onClick={() => verschieben(t, idx, -1)}><FaArrowUp /></button>
                    <button className="me-btn me-btn-icon" title="Nach unten" disabled={idx === t.bausteine.length - 1}
                            onClick={() => verschieben(t, idx, +1)}><FaArrowDown /></button>
                    <button className="me-btn me-btn-icon" title="Bearbeiten"
                            onClick={() => setEditor({ id: b.id, thema_id: t.id, titel: b.titel || "", betreff: b.betreff || "", text: b.text || "" })}>
                      <FaPen />
                    </button>
                    {!b.geschuetzt && (
                      <button className="me-btn me-btn-icon me-btn-danger" title="Löschen"
                              onClick={() => bausteinLoeschen(b)}><FaTrash /></button>
                    )}
                  </div>
                ))}
                {t.id != null && (
                  <button className="me-btn me-btn-secondary me-btn-sm" style={{ marginTop: 6 }}
                          onClick={() => setEditor({ thema_id: t.id, titel: "", betreff: "", text: "" })}>
                    <FaPlus /> Baustein
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {config && (
          <div style={{ border: "1px solid #334155", borderRadius: 8, padding: 10, marginTop: 4 }}>
            <b>Ablage in Dropbox</b>
            <label style={{ display: "block", marginTop: 6, fontSize: 13 }}>Regel (Platzhalter: {"{objekt_ordner} {vertrag_ordner}"})
              <input className="me-filter-input" style={{ width: "100%" }} value={config.ablage_muster || ""}
                     onChange={(e) => setConfig((p) => ({ ...p, ablage_muster: e.target.value }))} />
            </label>
            <label style={{ display: "block", marginTop: 6, fontSize: 13 }}>Sammelordner Kündigungen (Fallback)
              <input className="me-filter-input" style={{ width: "100%" }} value={config.ablage_fallback || ""}
                     onChange={(e) => setConfig((p) => ({ ...p, ablage_fallback: e.target.value }))} />
            </label>
            <label style={{ display: "block", marginTop: 6, fontSize: 13 }}>Sammelordner weitere Vorgänge (ohne Mietvertrag)
              <input className="me-filter-input" style={{ width: "100%" }} value={config.schreiben_ablage_fallback || ""}
                     onChange={(e) => setConfig((p) => ({ ...p, schreiben_ablage_fallback: e.target.value }))} />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <button className="me-btn me-btn-primary me-btn-sm" onClick={async () => {
                try {
                  const r = await fetch(`${KUENDIGUNG_API}/config`, { method: "PUT",
                    headers: { "Content-Type": "application/json" }, body: JSON.stringify(config) });
                  if (!r.ok) throw new Error("Speichern fehlgeschlagen");
                  setConfig(await r.json());
                } catch (e) { setFehler(String(e.message || e)); }
              }}>Ablage speichern</button>
            </div>
          </div>
        )}

        {editor && (
          <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
               onClick={() => setEditor(null)}>
            <div className="me-drawer" style={{ maxWidth: 640, width: "92vw", height: "auto", borderRadius: 12, padding: 18 }}
                 onClick={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>{editor.id ? "Baustein bearbeiten" : "Neuer Baustein"}</h3>
              <label style={{ display: "block", marginBottom: 8 }}>Titel
                <input className="me-filter-input" style={{ width: "100%" }} value={editor.titel}
                       onChange={(e) => setEditor((p) => ({ ...p, titel: e.target.value }))} />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>Betreff (Platzhalter wie {"{heute}"} erlaubt)
                <input className="me-filter-input" style={{ width: "100%" }} value={editor.betreff}
                       onChange={(e) => setEditor((p) => ({ ...p, betreff: e.target.value }))} />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>Text (**fett**, „- " Listen, Platzhalter {"{anrede}"}, {"{absender_anschrift}"}, {"{unterschrift}"} …)
                <textarea rows={12} style={{ width: "100%" }} value={editor.text}
                          onChange={(e) => setEditor((p) => ({ ...p, text: e.target.value }))} />
              </label>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="me-btn me-btn-secondary" onClick={() => setEditor(null)}>Abbrechen</button>
                <button className="me-btn me-btn-primary" disabled={!editor.titel.trim()} onClick={editorSpeichern}>Speichern</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
